import i18next from 'i18next'
import { List } from 'immutable'
import Plyr from 'plyr-react'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { Link, Option } from 'widgets-for-react'
import { BreadcrumbItem, BreadcrumbLayout } from '../../../utils/breadcrumbs'
import {
  ButtonGhost,
  ButtonTertiary,
  StartTrialButton,
} from '../../../utils/buttons'
import { formatDate } from '../../../utils/date'
import { blockInviewHandler } from '../../../utils/inview'
import { ModalLayout } from '../../../utils/modal'
import { componentDetailPath } from '../../../utils/pages'
import { PlyrSource } from '../../../utils/plyr'
import { SVGIcon } from '../../../utils/svgicon'
import { ComponentTeaserData } from '../components/componentTeasers/componentTeasersState'
import { homeInViewThreshold } from '../home/loggedOutHome/loggedOutHomeWidget'
import { PictureLayout } from '../image/imageLayout'
import { ImageData } from '../image/imageState'
import { routeUpdaters } from '../routes/routesState'
import { SponsorProps, SponsoredLayout } from '../sponsor/sponsorLayout'

type PageHeaderBaseProps = {
  title: string
  intro?: string
  breadcrumbs?: List<BreadcrumbItem>
  hasBorder?: boolean
}

type PageHeaderTextProps = Partial<{
  kind: 'default'
  additionalJSX?: JSX.Element
}> &
  PageHeaderBaseProps

type PageHeaderDashboardProps = {
  kind: 'dashboard'
  expandable?: {
    isExpanded: boolean
    onExpand: () => void
  }
} & PageHeaderBaseProps

type PageHeaderCollectionDetailProps = Partial<{
  kind: 'collection-detail'
  primaryAction: JSX.Element
  secondaryAction: JSX.Element
  onEdit?: () => void
  onDelete?: () => void
}> &
  PageHeaderBaseProps

type PageHeaderHomeProps = Partial<{
  kind: 'home'
  primaryAction: JSX.Element
  secondaryAction: JSX.Element
  image: ImageData
  srcSet?: string
  kpis: {
    title: string
    intro: string
    iconId: 'pan' | 'knife' | 'hexagon-sliced-open'
  }[]
}> &
  PageHeaderBaseProps

type PageHeaderHomeLoggedInProps = Partial<{
  kind: 'home-logged-in'
  firstName: string
  firstNameLoaded: boolean
  componentOfTheDay: ComponentTeaserData | 'loading'
}> &
  PageHeaderBaseProps

type PageHeaderBlogProps = {
  kind: 'blog'
  date?: Date
  image?: ImageData
  srcSet?: string
  sponsor?: SponsorProps
} & PageHeaderBaseProps

type PageHeaderDishProps = {
  kind: 'dish'
  image?: ImageData
  srcSet?: string
  sponsor?: SponsorProps
} & PageHeaderBaseProps

type PageHeaderKnowledgeProps = {
  kind: 'knowledge'
  date?: Date
  image?: ImageData
  srcSet?: string
  sponsor?: SponsorProps
} & PageHeaderBaseProps

type PageHeaderComponentEditProps = Partial<{
  kind: 'component-edit'
}> &
  PageHeaderBaseProps

export type PageHeaderProps =
  | PageHeaderBlogProps
  | PageHeaderCollectionDetailProps
  | PageHeaderDashboardProps
  | PageHeaderDishProps
  | PageHeaderHomeProps
  | PageHeaderHomeLoggedInProps
  | PageHeaderKnowledgeProps
  | PageHeaderComponentEditProps
  | PageHeaderTextProps

export const PublicHomePageHeader = (p: {
  videoOpened: boolean
  openVideo: () => void
  closeVideo: () => void
  video: Option<PlyrSource>
}) =>
  PageHeaderLayout({
    kind: 'home',
    title: i18next.t('home:pageHeaderTitle'),
    intro: i18next.t('home:pageHeaderIntro'),
    primaryAction: <StartTrialButton />,
    secondaryAction: (
      <>
        <ButtonTertiary
          kind="button"
          size="m"
          label={i18next.t('home:pageHeaderWatchVideo')}
          iconId="play"
          iconSize="18"
          onClick={p.openVideo}
        />
        {p.videoOpened && (
          <ModalLayout
            onHide={p.closeVideo}
            modifier="video"
            title={i18next.t('home:pageHeaderVideoTitle')}
          >
            {p.video.kind == 'r' ? (
              <figure className="figure--video">
                <div className="figure__media">
                  <Plyr
                    source={p.video.v}
                    ref={(ref) => {
                      // const onPlay = () => console.log('plyr play')
                      // const onPause = () => console.log('plyr pause')
                      // const onEnded = () => console.log('plyr end')
                      // ref?.plyr?.on('play', onPlay)
                      // ref?.plyr?.on('pause', onPause)
                      // ref?.plyr?.on('ended', onEnded)
                    }}
                  />
                </div>
              </figure>
            ) : (
              <p>{i18next.t('common:videoCannotBePlayed')}</p>
            )}
          </ModalLayout>
        )}
      </>
    ),
    image: {
      Url: '/spa/images/dish-on-counter-small.jpg',
      Title: i18next.t('home:pageHeaderImageAlt'),
    },
    srcSet:
      '/spa/images/dish-on-counter-small.jpg 480w, /spa/images/dish-on-counter-medium.jpg 960w, /spa/images/dish-on-counter-large.jpg 1920w',
    kpis: [
      {
        title: i18next.t('home:kpiPrimaryTitle'),
        intro: i18next.t('home:kpiPrimaryText'),
        iconId: 'hexagon-sliced-open',
      },
      {
        title: i18next.t('home:kpiSecondaryTitle'),
        intro: i18next.t('home:kpiSecondaryText'),
        iconId: 'pan',
      },
      {
        title: i18next.t('home:kpiTertiaryTitle'),
        intro: i18next.t('home:kpiTertiaryText'),
        iconId: 'knife',
      },
    ],
  })

export const DashboardPageHeader = (p: PageHeaderDashboardProps) =>
  PageHeaderLayout({
    kind: 'dashboard',
    hasBorder: true,
    title: p.title,
    breadcrumbs: p.breadcrumbs,
    expandable: p.expandable && {
      isExpanded: p.expandable.isExpanded,
      onExpand: p.expandable.onExpand,
    },
  })

export const CollectionDetailPageHeader = (
  p: PageHeaderCollectionDetailProps
) =>
  PageHeaderLayout({
    kind: 'collection-detail',
    title: p.title,
    intro: p.intro,
    breadcrumbs: p.breadcrumbs,
    primaryAction: p.onEdit ? (
      <ButtonGhost
        kind="button"
        size="s"
        label={i18next.t('common:edit')}
        iconId="edit"
        iconSize="22"
        onClick={p.onEdit}
      />
    ) : (
      <></>
    ),
    secondaryAction: p.onDelete ? (
      <ButtonGhost
        kind="button"
        size="s"
        label={i18next.t('common:delete')}
        iconId="delete"
        iconSize="22"
        onClick={p.onDelete}
      />
    ) : (
      <></>
    ),
  })

export const TextPageHeader = (p: PageHeaderTextProps) =>
  PageHeaderLayout({
    kind: 'default',
    title: p.title,
    intro: p.intro || '',
    breadcrumbs: p.breadcrumbs,
    hasBorder: p.hasBorder,
    additionalJSX: p.additionalJSX,
  })

export const PageHeaderLayout = (p: PageHeaderProps) => {
  const isHorizontal = p.kind == 'home' || p.kind == 'blog'
  const isVertical = p.kind == 'knowledge' || p.kind == 'dish'
  const hasSponsor =
    (p.kind == 'blog' || p.kind == 'knowledge' || p.kind == 'dish') && p.sponsor
  const hasBorder = p.hasBorder || p.kind == 'dashboard'

  const sharedTagProps = {
    className: `pageheader pageheader--${p.kind} pageheader--${
      isHorizontal ? 'horizontal' : isVertical ? 'vertical' : 'text'
    }${hasSponsor ? ' has-sponsor' : ''}${
      p.breadcrumbs ? ' has-breadcrumbs' : ''
    }${hasBorder ? ' has-border' : ''}`,
  }

  return (
    <>
      {p.kind == 'home-logged-in' &&
      (!p.firstNameLoaded || p.componentOfTheDay == 'loading') ? (
        <header {...sharedTagProps}>
          <PageHeaderInner {...p} />
        </header>
      ) : (
        <InView
          {...sharedTagProps}
          as="header"
          triggerOnce={true}
          threshold={p.kind == 'home' ? homeInViewThreshold : 0.1}
          onChange={(isInView, entry) => {
            // On 'home-logged-in', set small inview delay
            if (p.kind == 'home-logged-in') {
              if (!p.firstNameLoaded && p.componentOfTheDay == 'loading') {
                setTimeout(
                  () => blockInviewHandler(isInView, entry.target),
                  100
                )
              }
            }
            blockInviewHandler(isInView, entry.target)
          }}
        >
          <PageHeaderInner {...p} />
        </InView>
      )}

      {p.kind == 'home' && p.kpis && (
        <InView
          as="section"
          triggerOnce={true}
          threshold={0.1}
          className="pageheader__kpis kpis bg--secondary"
          onChange={(isInView, entry) =>
            blockInviewHandler(isInView, entry.target)
          }
        >
          <div className="container--m gutter--m">
            <h2 className="sr-text">{i18next.t('home:kpisTitle')}</h2>
            <ul className="kpi__list">
              {p.kpis.map((kpi) => (
                <li key={kpi.title} className="kpi__item">
                  <p className="kpi__header">
                    <span className="kpi__title title--xs">{kpi.title}</span>
                    <SVGIcon
                      className="kpi__icon"
                      iconId={kpi.iconId}
                      size=""
                    />
                  </p>
                  <p className="kpi__intro">{kpi.intro}</p>
                </li>
              ))}
            </ul>
          </div>
        </InView>
      )}
    </>
  )
}

const PageHeaderInner = (props: PageHeaderProps) => (
  <div
    className={`pageheader__inner gutter--m ${
      props.kind == 'component-edit' ? 'container--s' : 'container--m '
    }`}
  >
    <div className="pageheader__text">
      <h1
        className={`pageheader__title ${
          props.kind == 'dashboard' ? 'data-hj-suppress' : ''
        }`}
        dangerouslySetInnerHTML={{ __html: props.title }}
      />

      {/* Breadcrumbs */}
      {props.breadcrumbs && <BreadcrumbLayout items={props.breadcrumbs} />}

      {/* Account expandable */}
      {props.kind === 'dashboard' && props.expandable && (
        <AccountExpandable {...props.expandable} />
      )}

      {/* Sponsor */}
      {(props.kind == 'blog' ||
        props.kind == 'knowledge' ||
        props.kind == 'dish') &&
        props.sponsor && <SponsoredLayout {...props.sponsor} />}

      {/* Component of the day */}
      {props.kind == 'home-logged-in' &&
        props.componentOfTheDay != 'loading' &&
        props.componentOfTheDay != undefined && (
          <p className="pageheader__intro block__intro--840">
            <span>{i18next.t('home:loggedInPageheaderIntro')}</span>{' '}
            <Link
              to={componentDetailPath(
                props.componentOfTheDay.Id,
                props.componentOfTheDay.Name
              )}
              className="link--inverted"
            >
              <span>{props.componentOfTheDay.Name}</span>
            </Link>
          </p>
        )}

      {props.intro && (
        <p
          className={`pageheader__intro${
            props.kind !== 'home' && props.kind !== 'blog'
              ? ' block__intro--840'
              : ''
          }`}
        >
          {props.intro}
        </p>
      )}

      {props.kind == 'default' && props.additionalJSX && (
        <div className="pageheader__intro block__intro--840">
          {props.additionalJSX}
        </div>
      )}

      {/* Date - Blogs without an image look like knowledge detail pages - but they need to render the publish date */}
      {(props.kind == 'blog' || props.kind == 'knowledge') && props.date && (
        <time
          dateTime={formatDate.ISO(props.date)}
          className="pageheader__date text--s"
        >
          <span className="sr-text">{`${i18next.t(
            'common:publishDate'
          )}: `}</span>
          {formatDate.DDMonthYYYY(i18next, props.date)}
        </time>
      )}

      {(props.kind == 'home' || props.kind == 'collection-detail') &&
        (props.primaryAction || props.secondaryAction) && (
          <div className="pageheader__footer block__clickables">
            <div className="pageheader__clickable block__clickable">
              {props.primaryAction}
            </div>

            {props.secondaryAction && (
              <div className="pageheader__clickable block__clickable">
                {props.secondaryAction}
              </div>
            )}
          </div>
        )}
    </div>

    {/* Component of the day image */}
    {props.kind == 'home-logged-in' &&
      props.componentOfTheDay != 'loading' &&
      props.componentOfTheDay != undefined && (
        <div className="pageheader__visual">
          <PictureLayout.Img
            image={props.componentOfTheDay.ImageURL}
            extraClasses="pageheader__image"
            width="960"
            height="960"
            lazyload={false}
          />
        </div>
      )}

    {/* Images */}
    {(props.kind == 'home' || props.kind == 'blog') &&
      props.image != undefined &&
      props.image.Url != '' && (
        <div className="pageheader__visual fit-object">
          <PictureLayout.Img
            image={props.image}
            extraClasses="pageheader__image"
            srcSet={props.srcSet}
            sizes={
              props.srcSet
                ? '(min-width: 64rem) 50vw, (min-width: 48rem) 40vw, 100vw'
                : undefined
            }
            width="960"
            height="800"
            lazyload={false}
          />
        </div>
      )}

    {(props.kind == 'knowledge' || props.kind == 'dish') &&
      props.image != undefined &&
      props.image.Url != '' && (
        <div className="pageheader__visual">
          <PictureLayout.Img
            image={props.image}
            extraClasses="pageheader__image"
            srcSet={props.srcSet}
            sizes={
              props.srcSet
                ? '(min-width: 64rem) 50vw, (min-width: 48rem) 40vw, 100vw'
                : undefined
            }
            width="720"
            height="480"
            lazyload={false}
          />
        </div>
      )}
  </div>
)

const AccountExpandable = (p: PageHeaderDashboardProps['expandable']) => {
  return p ? (
    <div className="pageheader__expand expand expand--account">
      <button
        id="a11y-account-button"
        onClick={p.onExpand}
        title={i18next.t('a11y:navigateAccount')}
        className="expand__button text--s"
        aria-controls="a11y-account-collapse"
        aria-expanded={p.isExpanded}
      >
        <SVGIcon className="button__icon" size={20} iconId="settings" />
        <span className="button__label">
          {i18next.t('auth:account')}
        </span>
        <SVGIcon className="expand__toggle" size={10} iconId="chevron-down" />
      </button>
      <nav
        id="a11y-account-collapse"
        aria-hidden={!p.isExpanded}
        className="expand__sub"
        aria-label={i18next.t('auth:accountSettings')}
      >
        <ul>
          <li key="account-details">
            <Link to={routeUpdaters(i18next.language).accountDetails.url}>
              <span>{i18next.t('auth:accountDetails')}</span>
            </Link>
          </li>
          <li key="log-out">
            <Link to={routeUpdaters(i18next.language).logout.url}>
              <span>{i18next.t('auth:logoutSubmit')}</span>
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  ) : (
    <></>
  )
}

export const convertEmToPx = (em: number) =>
  parseFloat(getComputedStyle(document.documentElement).fontSize) * em
